import React, {FC, useEffect, useState} from 'react'

export type ApprovalAction = 'approve' | 'reject'

interface FindingApprovalActionModalProps {
  visible: boolean
  action: ApprovalAction
  findingName?: string
  onClose: () => void
  onConfirm: (shoreRemarks: string, rejectionRemark: string) => void | Promise<void>
  isLoading?: boolean
}

const FindingApprovalActionModal: FC<FindingApprovalActionModalProps> = ({
  visible,
  action,
  findingName,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [shoreRemarks, setShoreRemarks] = useState('')
  const [rejectionRemark, setRejectionRemark] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (visible) {
      setShoreRemarks('')
      setRejectionRemark('')
      setError('')
    }
  }, [visible])

  if (!visible) return null

  const isApprove = action === 'approve'
  const title = isApprove ? 'Approve Finding' : 'Reject Finding'
  const btnClass = isApprove ? 'btn btn-success btn-sm' : 'btn btn-danger btn-sm'
  const btnLabel = isApprove ? 'Approve' : 'Reject'

  const handleConfirm = async () => {
    if (!isApprove && !rejectionRemark.trim()) {
      setError('Rejection remark is required')
      return
    }
    setError('')
    await onConfirm(shoreRemarks, rejectionRemark)
  }

  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{width: '500px', maxWidth: '95vw'}}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0 fw-bold'>
            {isApprove ? '✅' : '❌'} {title}
          </h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='px-4 py-4'>
          {findingName && (
            <p className='text-muted mb-4'>
              Finding: <strong>{findingName}</strong>
            </p>
          )}

          <div className='mb-3'>
            <label className='form-label fw-semibold fs-6 mb-2'>
              Shore Remarks
              <span className='text-muted ms-2 fw-normal fs-7'>(optional)</span>
            </label>
            <textarea
              className='form-control'
              rows={3}
              value={shoreRemarks}
              onChange={(e) => setShoreRemarks(e.target.value)}
              placeholder='Add any notes from the shore team...'
              disabled={isLoading}
            />
          </div>

          {!isApprove && (
            <div className='mb-3'>
              <label className='form-label fw-semibold fs-6 mb-2'>
                Rejection Reason <span className='text-danger ms-1'>*</span>
              </label>
              <textarea
                className={`form-control ${error ? 'is-invalid' : ''}`}
                rows={3}
                value={rejectionRemark}
                onChange={(e) => {
                  setRejectionRemark(e.target.value)
                  if (error) setError('')
                }}
                placeholder='Explain why this finding is being rejected...'
                disabled={isLoading}
              />
              {error && <div className='invalid-feedback'>{error}</div>}
            </div>
          )}

          {isApprove && (
            <div className='alert alert-success py-2 px-3 small'>
              Approving will mark this finding as <strong>Approved</strong> and close it.
            </div>
          )}

          {!isApprove && (
            <div className='alert alert-warning py-2 px-3 small'>
              Rejecting will send this finding back to the crew for revision.
            </div>
          )}
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-light btn-sm'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type='button'
            className={btnClass}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className='spinner-border spinner-border-sm me-1' />
                Processing...
              </>
            ) : (
              btnLabel
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FindingApprovalActionModal
